/**
 * Deal Room Detail Page - Phase 6 Shared Deal Rooms.
 * Tabs: Documents, Access, Activity, Messages, Settings.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ArrowLeft,
  Download,
  FileText,
  FolderLock,
  Loader2,
  MessageCircle,
  Save,
  Send,
  Shield,
  ShieldCheck,
  Trash2,
  Upload,
  UserPlus,
  Users,
  BarChart3,
  History,
  Settings as SettingsIcon,
  Link2 as LinkIcon,
  Layers,
  Sparkles,
} from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import dealRoomService from '../services/dealRoomService';
import {
  AccessTier,
  AccessTierMap,
  DealRoom,
  DealRoomAccess,
  DealRoomActivity,
  DealRoomDocument,
  DealRoomFileType,
  DealRoomMessage,
  DealRoomStatus,
  InvestorEngagementSummary,
  MessageSenderType,
} from '../types/dealRoom';
import DealRoomActivityHeatmap from '../components/DealRoomActivityHeatmap';
import GrantAccessModal from '../components/GrantAccessModal';
import DealStrategyView, { DealStrategyJson } from '../components/DealStrategyView';
import UploadDocumentModal from '../components/UploadDocumentModal';

type TabId = 'documents' | 'access' | 'strategy' | 'activity' | 'messages' | 'settings';

const TABS: Array<{ id: TabId; label: string; icon: React.ComponentType<{ className?: string }> }> = [
  { id: 'documents', label: 'Documents', icon: FileText },
  { id: 'access', label: 'Access', icon: Users },
  { id: 'strategy', label: 'Strategy', icon: Layers },
  { id: 'activity', label: 'Activity', icon: BarChart3 },
  { id: 'messages', label: 'Messages', icon: MessageCircle },
  { id: 'settings', label: 'Settings', icon: SettingsIcon },
];

const TIERS: AccessTier[] = ['basic', 'qualified', 'lead'];

const tierBadge = (t: AccessTier): string => {
  switch (t) {
    case 'basic':
      return 'bg-gray-100 text-gray-700';
    case 'qualified':
      return 'bg-blue-100 text-blue-700';
    case 'lead':
      return 'bg-purple-100 text-purple-700';
  }
};

const statusBadge = (s: DealRoomStatus): string => {
  switch (s) {
    case 'draft':
      return 'bg-gray-100 text-gray-700 border-gray-300';
    case 'active':
      return 'bg-green-100 text-green-700 border-green-300';
    case 'closed':
      return 'bg-blue-100 text-blue-700 border-blue-300';
    case 'archived':
      return 'bg-amber-100 text-amber-700 border-amber-300';
  }
};

const senderColor = (t: MessageSenderType): string => {
  switch (t) {
    case 'admin':
      return 'bg-blue-100 text-blue-800';
    case 'investor':
      return 'bg-purple-100 text-purple-800';
    case 'founder':
      return 'bg-green-100 text-green-800';
  }
};

const formatBytes = (n?: number | null): string => {
  if (n == null) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
};

const formatDateTime = (s?: string | null): string =>
  s ? new Date(s).toLocaleString() : '—';
const formatDate = (s?: string | null): string =>
  s ? new Date(s).toLocaleDateString() : '—';

const DealRoomDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const roomId = Number(id);

  const [room, setRoom] = useState<DealRoom | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<TabId>('documents');

  const [documents, setDocuments] = useState<DealRoomDocument[]>([]);
  const [accessList, setAccessList] = useState<DealRoomAccess[]>([]);
  const [activities, setActivities] = useState<DealRoomActivity[]>([]);
  const [messages, setMessages] = useState<DealRoomMessage[]>([]);

  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadDefaultFile, setUploadDefaultFile] = useState<File | null>(null);
  const [grantOpen, setGrantOpen] = useState(false);

  // Settings form
  const [formName, setFormName] = useState('');
  const [formStatus, setFormStatus] = useState<DealRoomStatus>('draft');
  const [formAccessJson, setFormAccessJson] = useState('');
  const [formNdaRequired, setFormNdaRequired] = useState(false);
  const [formNdaUrl, setFormNdaUrl] = useState('');
  const [savingSettings, setSavingSettings] = useState(false);

  // Message composer
  const [messageBody, setMessageBody] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);

  // Strategy
  const [strategy, setStrategy] = useState<DealStrategyJson | null>(null);
  const [strategyMeta, setStrategyMeta] = useState<{ version?: number; generated_at?: string } | null>(null);
  const [strategyLoaded, setStrategyLoaded] = useState(false);
  const [generatingStrategy, setGeneratingStrategy] = useState(false);

  const [dragActive, setDragActive] = useState(false);
  const dragCounter = useRef(0);

  const applyRoomData = (r: DealRoom) => {
    setRoom(r);
    if (r.documents) setDocuments(r.documents);
    if (r.access) setAccessList(r.access);
    if (r.recent_activity) setActivities(r.recent_activity);
    setFormName(r.room_name);
    setFormStatus(r.status);
    setFormAccessJson(JSON.stringify(r.access_tiers || { basic: [], qualified: [], lead: [] }, null, 2));
    setFormNdaRequired(!!r.nda_required);
    setFormNdaUrl(r.nda_template_url || '');
  };

  const loadRoom = async () => {
    if (!roomId) {
      toast.error('Invalid deal room id');
      setLoading(false);
      return;
    }
    setLoading(true);
    const res = await dealRoomService.fetchDealRoom(roomId);
    if (res.error) {
      toast.error(res.error);
    } else if (res.data) {
      applyRoomData(res.data);
    }
    setLoading(false);
  };

  const loadDocuments = async () => {
    const res = await dealRoomService.fetchDocuments(roomId);
    if (res.data) setDocuments(res.data);
  };

  const loadActivity = async () => {
    const res = await dealRoomService.fetchActivity(roomId);
    if (res.data) setActivities(res.data);
  };

  const loadMessages = async () => {
    const res = await dealRoomService.fetchMessages(roomId);
    if (res.data) setMessages(res.data);
  };

  const loadStrategy = async () => {
    const res = await dealRoomService.fetchStrategy(roomId);
    setStrategyLoaded(true);
    if (res.data) {
      setStrategy(res.data.strategy_json || null);
      setStrategyMeta({ version: res.data.version, generated_at: res.data.generated_at });
    } else {
      setStrategy(null);
      setStrategyMeta(null);
    }
  };

  const handleGenerateStrategy = async () => {
    setGeneratingStrategy(true);
    const res = await dealRoomService.generateStrategy(roomId);
    setGeneratingStrategy(false);
    if (res.error || !res.data) {
      toast.error(res.error || 'Failed to generate strategy');
      return;
    }
    setStrategy(res.data.strategy_json || null);
    setStrategyMeta({ version: res.data.version, generated_at: res.data.generated_at });
    toast.success(`Strategy generated (v${res.data.version})`);
  };

  useEffect(() => {
    loadRoom();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId]);

  useEffect(() => {
    if (!room) return;
    if (tab === 'activity') loadActivity();
    if (tab === 'messages') loadMessages();
    if (tab === 'strategy' && !strategyLoaded) loadStrategy();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, room]);

  const handleUpload = async (payload: {
    file: File;
    access_tier: AccessTier;
    file_type: DealRoomFileType;
    previous_document_id?: number;
  }) => {
    const res = await dealRoomService.uploadDocument(roomId, payload);
    if (res.error) {
      toast.error(res.error);
      return;
    }
    toast.success('Document uploaded');
    setUploadOpen(false);
    setUploadDefaultFile(null);
    await loadDocuments();
  };

  const handleDownload = async (doc: DealRoomDocument) => {
    const res = await dealRoomService.downloadDocument(doc.id);
    if (res.error) {
      toast.error(res.error);
      return;
    }
    if (res.data) {
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.file_name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    }
  };

  const handleGrantAccess = async (payload: {
    investor_id: number;
    access_tier: AccessTier;
    nda_required: boolean;
  }) => {
    const res = await dealRoomService.grantAccess(roomId, payload);
    if (res.error) {
      toast.error(res.error);
      return;
    }
    toast.success('Access granted');
    setGrantOpen(false);
    await loadRoom();
  };

  const handleRevokeAccess = async (accessId: number) => {
    if (!window.confirm('Revoke access for this investor?')) return;
    const res = await dealRoomService.revokeAccess(accessId);
    if (res.error) {
      toast.error(res.error);
      return;
    }
    toast.success('Access revoked');
    setAccessList((list) => list.filter((a) => a.id !== accessId));
  };

  // Mint a read-only view link and copy it to the clipboard. Investors
  // need an existing access grant; the startup link is tied to the
  // room's company.
  const shareViewLink = async (
    party: 'investor' | 'founder',
    investorId?: number
  ) => {
    const res = await dealRoomService.createViewLink(roomId, {
      party,
      investor_id: investorId,
    });
    if (res.error || !res.data) {
      toast.error(res.error || 'Failed to create link');
      return;
    }
    try {
      await navigator.clipboard.writeText(res.data.url);
      toast.success(
        `${party === 'investor' ? 'Investor' : 'Startup'} link copied — expires in ${res.data.expires_in_days} days`
      );
    } catch {
      // Clipboard blocked — surface the URL so the admin can copy manually.
      window.prompt('Copy this read-only deal-room link:', res.data.url);
    }
  };

  const handleUpdateAccessTier = async (
    accessId: number,
    newTier: AccessTier
  ) => {
    const res = await dealRoomService.updateAccess(accessId, {
      access_tier: newTier,
    });
    if (res.error) {
      toast.error(res.error);
      return;
    }
    if (res.data)
      setAccessList((list) =>
        list.map((a) => (a.id === accessId ? res.data! : a))
      );
  };

  const handleDeleteRoom = async () => {
    if (
      !window.confirm('Delete this deal room? This cannot be undone.')
    )
      return;
    const res = await dealRoomService.deleteDealRoom(roomId);
    if (res.error) {
      toast.error(res.error);
      return;
    }
    toast.success('Deal room deleted');
    navigate('/deal-rooms');
  };

  const handleSaveSettings = async () => {
    let accessTiers: AccessTierMap;
    try {
      const parsed = JSON.parse(formAccessJson);
      accessTiers = {
        basic: Array.isArray(parsed?.basic) ? parsed.basic : [],
        qualified: Array.isArray(parsed?.qualified) ? parsed.qualified : [],
        lead: Array.isArray(parsed?.lead) ? parsed.lead : [],
      };
    } catch {
      toast.error('Access tiers must be valid JSON');
      return;
    }
    setSavingSettings(true);
    const res = await dealRoomService.updateDealRoom(roomId, {
      room_name: formName,
      status: formStatus,
      access_tiers: accessTiers,
      nda_required: formNdaRequired,
      nda_template_url: formNdaUrl || null,
    });
    setSavingSettings(false);
    if (res.error) {
      toast.error(res.error);
      return;
    }
    if (res.data) {
      applyRoomData(res.data);
      toast.success('Settings saved');
    }
  };

  const handleSendMessage = async () => {
    const body = messageBody.trim();
    if (!body) return;
    setSendingMessage(true);
    const res = await dealRoomService.sendMessage(roomId, {
      sender_type: 'admin',
      sender_id: 'admin',
      body,
    });
    setSendingMessage(false);
    if (res.error) {
      toast.error(res.error);
      return;
    }
    setMessageBody('');
    await loadMessages();
  };

  // Drag-and-drop upload
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    dragCounter.current = 0;
    const file = e.dataTransfer?.files?.[0];
    if (file) {
      setUploadDefaultFile(file);
      setUploadOpen(true);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current += 1;
    if (e.dataTransfer?.types?.includes('Files')) setDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current -= 1;
    if (dragCounter.current <= 0) {
      dragCounter.current = 0;
      setDragActive(false);
    }
  };

  // Group documents by tier
  const docsByTier = useMemo(() => {
    const map: Record<AccessTier, DealRoomDocument[]> = {
      basic: [],
      qualified: [],
      lead: [],
    };
    documents.forEach((d) => {
      if (map[d.access_tier]) map[d.access_tier].push(d);
    });
    return map;
  }, [documents]);

  // Previous versions index (by previous_version_id -> replaces list)
  const versionsByPrevId = useMemo(() => {
    const map: Record<number, DealRoomDocument[]> = {};
    documents.forEach((d) => {
      if (d.previous_version_id != null) {
        if (!map[d.previous_version_id]) map[d.previous_version_id] = [];
        map[d.previous_version_id].push(d);
      }
    });
    return map;
  }, [documents]);

  // Engagement summary per investor from activities
  const engagementSummary: InvestorEngagementSummary[] = useMemo(() => {
    const byInv: Record<number, InvestorEngagementSummary> = {};
    activities.forEach((a) => {
      if (!byInv[a.investor_id]) {
        byInv[a.investor_id] = {
          investor_id: a.investor_id,
          investor_name: a.investor?.full_name,
          firm_name: a.investor?.firm_name,
          total_views: 0,
          total_downloads: 0,
          total_time_seconds: 0,
        };
      }
      const s = byInv[a.investor_id];
      if (a.activity_type === 'view') s.total_views += 1;
      if (a.activity_type === 'download') s.total_downloads += 1;
      if (a.duration_seconds) s.total_time_seconds += a.duration_seconds;
    });
    return Object.values(byInv);
  }, [activities]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="bg-white border border-gray-200 rounded-xl p-10 flex items-center justify-center text-gray-600">
          <Loader2 className="w-5 h-5 mr-3 animate-spin" />
          Loading deal room...
        </div>
      </DashboardLayout>
    );
  }

  if (!room) {
    return (
      <DashboardLayout>
        <div className="bg-white border border-gray-200 rounded-xl p-10 text-center text-gray-600">
          <p className="mb-4">Deal room not found.</p>
          <Link to="/deal-rooms" className="text-blue-600 hover:underline">
            Back to deal rooms
          </Link>
        </div>
      </DashboardLayout>
    );
  }

  const linkedName =
    room.project?.project_name ||
    room.startup?.name ||
    (room.project_id
      ? `Project #${room.project_id}`
      : room.startup_id
      ? `Startup #${room.startup_id}`
      : '—');

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Back link */}
        <Link
          to="/deal-rooms"
          className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back to deal rooms
        </Link>

        {/* Header */}
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="flex items-start gap-3 min-w-0 flex-1">
              <FolderLock className="w-6 h-6 text-blue-600 mt-1 flex-shrink-0" />
              <div className="min-w-0">
                <h1 className="text-xl font-bold text-gray-900 truncate">
                  {room.room_name}
                </h1>
                <div className="text-sm text-gray-600 mt-0.5">
                  <span className="text-gray-500">Linked:</span> {linkedName}
                  {room.pipeline_id && (
                    <>
                      {' '}
                      ·{' '}
                      <Link
                        to={`/deal-pipeline/${room.pipeline_id}`}
                        className="text-blue-600 hover:underline"
                      >
                        Pipeline #{room.pipeline_id}
                      </Link>
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <span
                className={`px-2.5 py-1 rounded border text-xs font-medium capitalize ${statusBadge(
                  room.status
                )}`}
              >
                {room.status}
              </span>
              {room.nda_required && (
                <span className="px-2.5 py-1 rounded bg-amber-100 text-amber-700 text-xs font-medium inline-flex items-center">
                  <Shield className="w-3 h-3 mr-1" />
                  NDA
                </span>
              )}
              {room.startup_id && (
                <button
                  onClick={() => shareViewLink('founder')}
                  className="inline-flex items-center px-3 py-1.5 border border-blue-300 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-50"
                  title="Copy a read-only view link for the startup/founder"
                >
                  <LinkIcon className="w-4 h-4 mr-1.5" />
                  Share with startup
                </button>
              )}
              <button
                onClick={handleDeleteRoom}
                className="inline-flex items-center px-3 py-1.5 border border-red-300 text-red-700 rounded-lg text-sm font-medium hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4 mr-1.5" />
                Delete
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex gap-2 overflow-x-auto">
            {TABS.map(({ id: tid, label, icon: Icon }) => (
              <button
                key={tid}
                onClick={() => setTab(tid)}
                className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                  tab === tid
                    ? 'border-blue-600 text-blue-700'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                <Icon className="w-4 h-4" />
                {label}
              </button>
            ))}
          </nav>
        </div>

        {/* ---------- Documents Tab ---------- */}
        {tab === 'documents' && (
          <div
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            className="space-y-4"
          >
            <div
              className={`border-2 border-dashed rounded-xl p-6 text-center transition-colors ${
                dragActive
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-300 bg-white'
              }`}
            >
              <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
              <p className="text-sm text-gray-600">
                Drag and drop a file anywhere in this tab, or
              </p>
              <button
                onClick={() => {
                  setUploadDefaultFile(null);
                  setUploadOpen(true);
                }}
                className="mt-2 inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
              >
                <Upload className="w-4 h-4 mr-2" />
                Upload document
              </button>
            </div>

            {TIERS.map((tier) => {
              const docs = docsByTier[tier];
              return (
                <div
                  key={tier}
                  className="bg-white border border-gray-200 rounded-xl p-4"
                >
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-gray-900 capitalize">
                      {tier} tier
                    </h3>
                    <span
                      className={`px-2 py-0.5 rounded text-xs font-medium ${tierBadge(
                        tier
                      )}`}
                    >
                      {docs.length} doc{docs.length === 1 ? '' : 's'}
                    </span>
                  </div>
                  {docs.length === 0 ? (
                    <div className="text-xs text-gray-400 py-3">
                      No documents in this tier.
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-100">
                      {docs
                        .filter((d) => d.previous_version_id == null)
                        .map((d) => {
                          const newerVersions = versionsByPrevId[d.id] || [];
                          return (
                            <div
                              key={d.id}
                              className="py-2 flex items-center gap-3"
                            >
                              <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
                              <div className="min-w-0 flex-1">
                                <div className="text-sm font-medium text-gray-900 truncate">
                                  {d.file_name}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {d.file_type} · v{d.version ?? 1} ·{' '}
                                  {formatBytes(d.file_size_bytes)} ·{' '}
                                  {formatDate(d.created_at)}
                                </div>
                                {newerVersions.length > 0 && (
                                  <details className="mt-1">
                                    <summary className="text-xs text-blue-600 cursor-pointer">
                                      {newerVersions.length} newer version
                                      {newerVersions.length === 1 ? '' : 's'}
                                    </summary>
                                    <div className="mt-1 space-y-1 pl-3 border-l border-gray-200">
                                      {newerVersions.map((nv) => (
                                        <div
                                          key={nv.id}
                                          className="flex items-center gap-2 text-xs text-gray-600"
                                        >
                                          <span className="truncate flex-1">
                                            v{nv.version ?? 2} — {nv.file_name}
                                          </span>
                                          <button
                                            onClick={() => handleDownload(nv)}
                                            className="text-blue-600 hover:underline"
                                          >
                                            Download
                                          </button>
                                        </div>
                                      ))}
                                    </div>
                                  </details>
                                )}
                              </div>
                              <button
                                onClick={() => handleDownload(d)}
                                className="inline-flex items-center px-2.5 py-1 border border-gray-300 rounded-lg text-xs hover:bg-gray-50"
                              >
                                <Download className="w-3.5 h-3.5 mr-1" />
                                Download
                              </button>
                            </div>
                          );
                        })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ---------- Access Tab ---------- */}
        {tab === 'access' && (
          <div className="space-y-4">
            <div className="flex justify-end">
              <button
                onClick={() => setGrantOpen(true)}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Grant access
              </button>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              {accessList.length === 0 ? (
                <div className="p-6 text-center text-gray-500 text-sm">
                  No investors have access yet.
                </div>
              ) : (
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left px-4 py-2 font-medium text-gray-700">
                        Investor
                      </th>
                      <th className="text-left px-4 py-2 font-medium text-gray-700">
                        Tier
                      </th>
                      <th className="text-left px-4 py-2 font-medium text-gray-700">
                        NDA
                      </th>
                      <th className="text-left px-4 py-2 font-medium text-gray-700">
                        Granted
                      </th>
                      <th className="text-right px-4 py-2 font-medium text-gray-700">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {accessList.map((a) => (
                      <tr key={a.id}>
                        <td className="px-4 py-2">
                          <div className="font-medium text-gray-900">
                            {a.investor?.full_name ||
                              `Investor #${a.investor_id}`}
                          </div>
                          {a.investor?.firm_name && (
                            <div className="text-xs text-gray-500">
                              {a.investor.firm_name}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-2">
                          <select
                            value={a.access_tier}
                            onChange={(e) =>
                              handleUpdateAccessTier(
                                a.id,
                                e.target.value as AccessTier
                              )
                            }
                            className={`px-2 py-0.5 rounded text-xs font-medium border border-gray-200 ${tierBadge(
                              a.access_tier
                            )}`}
                          >
                            {TIERS.map((t) => (
                              <option key={t} value={t}>
                                {t}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="px-4 py-2">
                          {a.nda_signed ? (
                            <span className="inline-flex items-center text-green-700 text-xs">
                              <ShieldCheck className="w-3.5 h-3.5 mr-1" />
                              Signed {formatDate(a.nda_signed_at)}
                            </span>
                          ) : (
                            <span className="inline-flex items-center text-amber-700 text-xs">
                              <Shield className="w-3.5 h-3.5 mr-1" />
                              Pending
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-xs text-gray-600">
                          {formatDate(a.granted_at)}
                        </td>
                        <td className="px-4 py-2 text-right whitespace-nowrap">
                          <button
                            onClick={() => shareViewLink('investor', a.investor_id)}
                            className="inline-flex items-center px-2 py-1 border border-blue-300 text-blue-700 rounded text-xs hover:bg-blue-50 mr-2"
                            title="Copy a read-only view link scoped to this investor's tier"
                          >
                            <LinkIcon className="w-3.5 h-3.5 mr-1" />
                            Share link
                          </button>
                          <button
                            onClick={() => handleRevokeAccess(a.id)}
                            className="inline-flex items-center px-2 py-1 border border-red-300 text-red-700 rounded text-xs hover:bg-red-50"
                          >
                            <Trash2 className="w-3.5 h-3.5 mr-1" />
                            Revoke
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {/* ---------- Strategy Tab ---------- */}
        {tab === 'strategy' && (
          <div className="space-y-4">
            <div className="flex items-start justify-between gap-3 bg-white border border-gray-200 rounded-xl p-5">
              <div>
                <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                  <Layers className="w-4 h-4 text-blue-600" />
                  Deal strategy
                </h2>
                <p className="text-xs text-gray-500 mt-1 max-w-xl">
                  AI-generated, milestone-based plan: capital released in
                  tranches as the startup hits targets, investor risk
                  mitigations, and concrete value-add steps. Shared
                  read-only with the investor + startup via their view link.
                  {strategyMeta?.version ? ` · v${strategyMeta.version}` : ''}
                </p>
              </div>
              <button
                onClick={handleGenerateStrategy}
                disabled={generatingStrategy}
                className="inline-flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 flex-shrink-0"
              >
                {generatingStrategy ? (
                  <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                ) : (
                  <Sparkles className="w-4 h-4 mr-1.5" />
                )}
                {strategy ? 'Regenerate' : 'Generate strategy'}
              </button>
            </div>

            {generatingStrategy && !strategy && (
              <div className="bg-white border border-gray-200 rounded-xl p-10 flex items-center justify-center text-gray-600">
                <Loader2 className="w-5 h-5 mr-3 animate-spin text-blue-600" />
                Generating strategy… (~20-40s)
              </div>
            )}

            {strategy ? (
              <DealStrategyView strategy={strategy} />
            ) : (
              !generatingStrategy && (
                <div className="bg-white border border-dashed border-gray-300 rounded-xl p-10 text-center">
                  <Layers className="w-8 h-8 mx-auto text-gray-300 mb-2" />
                  <p className="text-sm text-gray-600">
                    No strategy yet. Click <strong>Generate strategy</strong> to
                    have Claude draft a milestone-based, win-win plan from this
                    company's profile and the linked pipeline.
                  </p>
                </div>
              )
            )}
          </div>
        )}

        {/* ---------- Activity Tab ---------- */}
        {tab === 'activity' && (
          <div className="space-y-4">
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">
                Engagement heatmap (last 7 days)
              </h3>
              <DealRoomActivityHeatmap activities={activities} />
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">
                Engagement summary
              </h3>
              {engagementSummary.length === 0 ? (
                <div className="text-sm text-gray-500 py-3">
                  No engagement data yet.
                </div>
              ) : (
                <table className="min-w-full text-sm">
                  <thead className="border-b border-gray-200">
                    <tr>
                      <th className="text-left px-2 py-2 font-medium text-gray-700">
                        Investor
                      </th>
                      <th className="text-right px-2 py-2 font-medium text-gray-700">
                        Views
                      </th>
                      <th className="text-right px-2 py-2 font-medium text-gray-700">
                        Downloads
                      </th>
                      <th className="text-right px-2 py-2 font-medium text-gray-700">
                        Time
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {engagementSummary.map((s) => (
                      <tr key={s.investor_id}>
                        <td className="px-2 py-2">
                          <div className="font-medium text-gray-900">
                            {s.investor_name || `Investor #${s.investor_id}`}
                          </div>
                          {s.firm_name && (
                            <div className="text-xs text-gray-500">
                              {s.firm_name}
                            </div>
                          )}
                        </td>
                        <td className="text-right px-2 py-2">
                          {s.total_views}
                        </td>
                        <td className="text-right px-2 py-2">
                          {s.total_downloads}
                        </td>
                        <td className="text-right px-2 py-2">
                          {Math.round(s.total_time_seconds / 60)} min
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">
                Activity log
              </h3>
              {activities.length === 0 ? (
                <div className="text-center py-6 text-gray-400 text-sm">
                  <History className="w-6 h-6 mx-auto mb-2 opacity-50" />
                  No activity yet.
                </div>
              ) : (
                <div className="space-y-2">
                  {activities.map((a) => {
                    const doc = a.document_id
                      ? documents.find((d) => d.id === a.document_id)
                      : null;
                    return (
                      <div
                        key={a.id}
                        className="flex items-start gap-3 text-sm border-b border-gray-100 pb-2"
                      >
                        <div className="text-xs text-gray-400 w-32 flex-shrink-0">
                          {formatDateTime(a.created_at)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-gray-900">
                            <span className="font-medium">
                              {a.investor?.full_name ||
                                `Investor #${a.investor_id}`}
                            </span>{' '}
                            <span className="text-gray-600">
                              {a.activity_type}
                            </span>
                            {doc && (
                              <>
                                {' '}
                                <span className="text-gray-600">—</span>{' '}
                                <span className="text-gray-700">
                                  {doc.file_name}
                                </span>
                              </>
                            )}
                          </div>
                          {a.duration_seconds != null && (
                            <div className="text-xs text-gray-500">
                              {a.duration_seconds}s
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ---------- Messages Tab ---------- */}
        {tab === 'messages' && (
          <div className="bg-white border border-gray-200 rounded-xl flex flex-col h-[70vh]">
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {messages.length === 0 ? (
                <div className="text-center text-sm text-gray-500 py-10">
                  <MessageCircle className="w-6 h-6 mx-auto mb-2 opacity-50" />
                  No messages yet. Start a conversation below.
                </div>
              ) : (
                messages.map((m) => (
                  <div key={m.id} className="flex gap-3">
                    <span
                      className={`px-2 py-0.5 rounded text-xs font-medium h-fit capitalize ${senderColor(
                        m.sender_type
                      )}`}
                    >
                      {m.sender_type}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-gray-400">
                        {formatDateTime(m.created_at)}
                      </div>
                      <div className="text-sm text-gray-900 whitespace-pre-wrap">
                        {m.body}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
            <div className="border-t border-gray-200 p-3 flex gap-2">
              <input
                type="text"
                value={messageBody}
                onChange={(e) => setMessageBody(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
                placeholder="Type a message... (admin)"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <button
                onClick={handleSendMessage}
                disabled={sendingMessage || !messageBody.trim()}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
              >
                {sendingMessage ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>
        )}

        {/* ---------- Settings Tab ---------- */}
        {tab === 'settings' && (
          <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-4 max-w-2xl">
            <div>
              <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1">
                Room name
              </label>
              <input
                type="text"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1">
                Status
              </label>
              <select
                value={formStatus}
                onChange={(e) =>
                  setFormStatus(e.target.value as DealRoomStatus)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="draft">Draft</option>
                <option value="active">Active</option>
                <option value="closed">Closed</option>
                <option value="archived">Archived</option>
              </select>
            </div>

            <div>
              <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1">
                Access tiers (JSON)
              </label>
              <textarea
                value={formAccessJson}
                onChange={(e) => setFormAccessJson(e.target.value)}
                rows={8}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs font-mono focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Shape: {`{"basic": [...], "qualified": [...], "lead": [...]}`}
              </p>
            </div>

            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={formNdaRequired}
                onChange={(e) => setFormNdaRequired(e.target.checked)}
              />
              Require NDA before access
            </label>

            <div>
              <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1">
                NDA template URL
              </label>
              <input
                type="url"
                value={formNdaUrl}
                onChange={(e) => setFormNdaUrl(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="https://..."
              />
            </div>

            <div className="flex justify-end">
              <button
                onClick={handleSaveSettings}
                disabled={savingSettings}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
              >
                {savingSettings ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Save settings
              </button>
            </div>
          </div>
        )}
      </div>

      <UploadDocumentModal
        open={uploadOpen}
        onClose={() => {
          setUploadOpen(false);
          setUploadDefaultFile(null);
        }}
        onConfirm={handleUpload}
        existingDocuments={documents}
        defaultFile={uploadDefaultFile}
      />
      <GrantAccessModal
        open={grantOpen}
        onClose={() => setGrantOpen(false)}
        onConfirm={handleGrantAccess}
      />
    </DashboardLayout>
  );
};

export default DealRoomDetailPage;
